import React, { FC, useEffect } from 'react'
import { Font, Heading4 } from 'is24-corecss'
import { useDispatch, useSelector } from 'react-redux'
import { FontsProps } from 'is24-corecss/build/@types/Fonts/FontsProps'
import { IconS24UserTie24 } from '@is24/cosma-ui-icons'
import { StepTemplate } from '../../components/styled/stepTemplate/StepTemplate'
import { ResponseIcon } from '../../components/icons/ResponseIcon'
import ProductBox from '../../components/ProductBox/ProductBox'
import { Intent } from '../../@types/intent'
import './ResponseStep.scss'
import { RealEstateType } from '../../@types/realEstateType'
import { reportTrackingEvent } from '../../tracking/thunks'
import { useReportViewEvent } from '../../hooks'
import { Button } from '../../components/styled/CosmaButton/CosmaButton'
import { IntegrationType } from '../../@types/config'
import { enrichLead } from '../../store/lead/thunks'
import { EnrichLeadValuationType } from '../../@types/enrichLeadValuationtype'
import { ReduxState } from '../../store/reducers'
import { getIsPdfWordingEnabled } from '../../utils/pdf'
import { getRangeValuationString, valuationAsString } from '../../utils/valuation'
import { STEP } from '../../@types/state'

const tinyColor = require('tinycolor2')

export interface ResponseStepProps {
  goToNextStep: () => unknown
}

const head = {
  [Intent.MAKLER_SALE]: {
    headline: 'Passende Makler melden sich in Kürze',
    subHeadline: 'Du erhältst eine individuelle Beratung und kannst den präzisen Wert der Immobilie besprechen.',
  },
  [Intent.LET]: {
    headline: ' Passende Immobilienexperten melden sich in Kürze',
    subHeadline: 'Du erhältst eine optimale Beratung und Unterstützung rund um deinen Vermietungsprozess.',
  },
  [Intent.MAKLER_LET]: {
    headline: 'Anfrage gesendet',
    subHeadline: 'Ein passender Experte wird sich bei dir melden, um eine unverbindliche Beratung durchzuführen.',
  },
  [Intent.MAYBE_MAKLER_LET]: {
    headline: 'Anfrage gesendet',
    subHeadline: 'Ein passender Experte wird sich bei dir melden, um eine unverbindliche Beratung durchzuführen.',
  },
  [Intent.INDEPENDENT_LET]: {
    headline: 'Immobilie erfolgreich bewertet',
    subHeadline: 'Das Ergebnis erhältst du zusätzlich per E-Mail.',
  },
  [Intent.BUY]: {
    headline: 'Immobilie erfolgreich bewertet',
    subHeadline: '',
  },
  [Intent.RENT]: {
    headline: 'Immobilie erfolgreich bewertet',
    subHeadline: '',
  },
  [Intent.INDEPENDENT_SALE]: {
    headline: 'Anfrage gesendet',
    subHeadline: '',
  },
  [Intent.PARTIAL_SALE]: {
    headline: 'Anfrage gesendet',
    subHeadline: '',
  },
  [Intent.BUY_MAYBE_WITH_SELL_OPTION]: {
    headline: '',
    subHeadline: '',
  },
  [Intent.MAYBE_MAKLER_SALE]: {
    headline: 'Passende Makler melden sich in Kürze',
    subHeadline: 'Du erhältst eine individuelle Beratung und kannst den präzisen Wert der Immobilie besprechen.',
  },
  [Intent.BUY_WITH_SELL_OPTION]: {
    headline: '',
    subHeadline: '',
  },
  [Intent.INDEPENDENT_WITH_OFFER_SALE]: {
    headline: 'Anfrage gesendet',
    subHeadline: '',
  },
  [Intent.MAYBE_AGENT_SALE]: {
    headline: '',
    subHeadline: '',
  },
}

export const ResponseStep: FC<ResponseStepProps> = (props) => {
  useReportViewEvent({ pageTitle: 'response' })
  const {
    valuation,
    lead,
    config,
    flow: { wizardFlowType },
  } = useSelector((state: ReduxState) => state)
  const { range, value, prevRange } = valuation.data ?? { range: undefined, value: undefined, prevRange: undefined }
  const { min, max } = lead.rangeValuationFromAddress || range || {}
  const { id: leadId, intent, realEstateType } = lead
  const { theme, integrationType, illustration } = config
  const isIv24Integration = integrationType === IntegrationType.IMMOVERKAUF24
  const showHighlighter = !(integrationType === IntegrationType.EXTERNAL) && !isIv24Integration
  const fontColorLightGrayDefault = theme.text ? (theme.text as FontsProps['color']) : 'lightgray'
  const fontColorRegularDefault = theme.text ? (theme.text as FontsProps['color']) : 'regular'
  const isPdfWordingChangeEnabled = getIsPdfWordingEnabled(wizardFlowType, realEstateType)

  const dispatch = useDispatch()

  useEffect(() => {
    if (leadId) {
      dispatch(
        enrichLead({
          valuationForStep: STEP.RESPONSE,
          enrichValuationType: EnrichLeadValuationType.SINGLE_VALUATION,
          pageTitle: 'response',
          request: {
            id: leadId,
            flowComplete: true,
          },
        })
      )
    }
  }, [])

  if (!intent || !realEstateType) {
    // redirect to error page, once there is one
    return <></>
  }

  const getInstructionText = (intent: Intent) => {
    switch (intent) {
      case Intent.INDEPENDENT_SALE:
        return `Ein passender Experte wird sich bei dir melden, um eine professionelle und unverbindliche
        Verkaufsberatung durchzuführen.`
      case Intent.INDEPENDENT_WITH_OFFER_SALE:
        return 'Eine Liste passender Käufer erhältst du per E-Mail'
      default:
        return 'Das Ergebnis erhältst du per E-Mail.'
    }
  }

  const isEligibleForValuationPdf = () =>
    [Intent.MAKLER_SALE, Intent.MAYBE_MAKLER_SALE].includes(intent as Intent) &&
    realEstateType &&
    [RealEstateType.HOUSE, RealEstateType.APARTMENT, RealEstateType.LAND].includes(realEstateType)

  const getStatusMessageHeadLine = (realEstateType: RealEstateType, intent: Intent) => {
    if (realEstateType === RealEstateType.COMMERCIAL) {
      return 'Passende Makler melden sich in Kürze'
    }

    if (isEligibleForValuationPdf()) {
      return 'Makler melden sich in Kürze um den exakten Wert mit dir zu besprechen.'
    }

    if (realEstateType === RealEstateType.LAND && [Intent.MAKLER_SALE, Intent.MAYBE_MAKLER_SALE].includes(intent)) {
      return 'Grundstück erfolgreich bewertet'
    }

    if (realEstateType === RealEstateType.LAND && [Intent.INDEPENDENT_SALE].includes(intent)) {
      return 'Anfrage gesendet'
    }

    return head[intent].headline
  }

  const getStatusMessageSubHeadLine = (realEstateType: RealEstateType, intent: Intent) => {
    if (realEstateType === RealEstateType.COMMERCIAL) {
      return 'Passende Experten für Gewerbe-Immobilien melden sich in Kürze, um deine genauen Anforderungen zu bestimmen und dich entsprechend zu beraten.'
    }
    return head[intent].subHeadline
  }

  const getRealEstateTypeValueHeader = (realEstateType: RealEstateType) => {
    if (realEstateType === RealEstateType.LAND) {
      return 'Ungefährer Marktwert des Grundstücks'
    } else {
      return 'Ungefährer Marktwert der Immobilie'
    }
  }

  const getRealEstateTypeValueHeaderLong = (realEstateType: RealEstateType) => {
    if (realEstateType === RealEstateType.LAND) {
      return 'Der geschätzte Wert deines Grundstücks liegt'
    } else {
      return 'Der geschätzte Wert deiner Immobilie liegt'
    }
  }

  const clickDownloadPDF = () => {
    dispatch(
      reportTrackingEvent({
        pageTitle: 'response',
        label: 'pdf-button_clicked',
        action: 'realtor-leadengine',
        category: 'residential',
        eventName: 'file_download',
        inputValue: 'button_click',
      })
    )
    props.goToNextStep()
  }

  const headline = isEligibleForValuationPdf() ? (
    <div className='grid grid-flex grid-justify-center'>
      <div className='grid-item palm-one-whole lap-two-third desk-two-third align-center headline-container'>
        <div className='headline'>{getStatusMessageHeadLine(realEstateType, intent)}</div>
      </div>
    </div>
  ) : (
    getStatusMessageHeadLine(realEstateType, intent)
  )

  const sellingPointCssClass = `grid-item palm-one-whole lap-one-half sellingPoint ${
    isIv24Integration ? 'desk-one-half' : 'desk-one-third'
  }`
  const hackToOverrideDefaultColorFromScopedCssForIV24: React.LegacyRef<HTMLElement> = (el) =>
    isIv24Integration && el && theme.primary && el.style.setProperty('color', theme.primary, 'important')

  return (
    <StepTemplate
      headline={headline}
      icon={isEligibleForValuationPdf() || isIv24Integration ? undefined : <ResponseIcon />}
      hideIconOnMobile={false}
      displayNext={false}
      backgroundColor={theme.background}
      textColor={theme.text}
      primary={theme.primary}
      illus={illustration}
    >
      {isEligibleForValuationPdf() && (
        <div data-testing='response-container'>
          <div className='grid-flex grid-justify-center'>
            <div className='grid-item palm-one-whole lap-one-half desk-one-third align-center '>
              <div data-testid='response-range-valuation' className='estimated-price-text'>
                <Heading4 tag='div' weight='bold' className='txt'>
                  {getRangeValuationString(min, max)}
                </Heading4>
                <Font color={fontColorLightGrayDefault}>Ungefähre Preisspanne</Font>
              </div>
            </div>
          </div>

          <div className='grid-flex grid-justify-center'>
            <div className={sellingPointCssClass}>
              <i className={'font-xl is24-icon-info1 info-icon'} ref={hackToOverrideDefaultColorFromScopedCssForIV24} />
              <div>
                <Font>
                  Eine verlässliche Marktwertermittlung kann nur von einem Experten vor Ort vorgenommen werden.
                </Font>
              </div>
            </div>
          </div>

          <div className='grid-flex grid-justify-center'>
            <div className='grid-item palm-one-whole lap-one-whole desk-one-third align-center'>
              <div className='center'>
                <div className='whats-next'>
                  <Heading4
                    tag='div'
                    weight='bold'
                    className={`${showHighlighter ? 'highlighter' : ''}`}
                    style={isIv24Integration ? { color: theme.primary } : {}}
                  >
                    So geht’s weiter
                  </Heading4>
                </div>
              </div>
            </div>
          </div>
          {!isIv24Integration && realEstateType !== RealEstateType.LAND && (
            <div className='grid grid-flex grid-justify-center'>
              <div className={sellingPointCssClass}>
                <i className='font-xl is24-icon-pdf icon ' />
                <div className='txt'>
                  <Font weight='bold' className='txthead' data-testid='pdf-teaser'>
                    {isPdfWordingChangeEnabled
                      ? 'Detaillierte Bewertung herunterladen'
                      : 'Bewertungs-PDF herunterladen'}
                  </Font>
                  <Font hint={true} color={fontColorLightGrayDefault} className='txtbody'>
                    Du erhältst eine erste Bewertung und weitere Informationen zu deinem Immobilienwert
                  </Font>
                </div>
              </div>
            </div>
          )}

          <div className='grid grid-flex grid-justify-center'>
            <div className={sellingPointCssClass}>
              <IconS24UserTie24 className='icon-profi icon' width={42} height={42} />
              <div className='txt'>
                <Font weight='bold' className='txthead'>
                  {realEstateType === RealEstateType.LAND
                    ? 'Grundstückswert mit Experten besprechen'
                    : 'Immobilienwert mit Experten besprechen'}
                </Font>
                <Font hint={true} color={fontColorLightGrayDefault} className='txtbody'>
                  {realEstateType === RealEstateType.LAND
                    ? 'Makler aus deiner Region melden sich bei dir und helfen bei der Grundstücksbewertung.'
                    : `Makler aus deiner Region melden sich bei dir und helfen bei der Immobilienbewertung.`}
                </Font>
              </div>
            </div>
          </div>

          <div className='grid grid-flex grid-justify-center'>
            <div className={sellingPointCssClass}>
              <i className='font-xl is24-icon-land-plot-house-build icon' />
              <div className='txt'>
                <Font weight='bold' className='txthead'>
                  Souverän in den Verkauf starten
                </Font>
                <Font hint={true} color={fontColorLightGrayDefault} className='txtbody'>
                  Der Makler unterstützt dich bei deinem individuellen Verkaufsvorhaben.
                </Font>
              </div>
            </div>
          </div>

          {!isIv24Integration && realEstateType !== RealEstateType.LAND && (
            <div className='grid grid-flex grid-justify-center'>
              <div className='grid-item align-center palm-one-whole desk-one-third'>
                <Button
                  onClick={clickDownloadPDF}
                  appearance='primary'
                  data-testid='generate-pdf-button'
                  primary={theme.primary}
                  textColor={!showHighlighter && tinyColor(theme?.primary).isDark() ? '#FFFFFF' : theme.text}
                  secondaryLight={theme.secondaryLight}
                >
                  <span className='is24-icon-pdf button-icon-margin-right dont-diplay-on-palm' />
                  {isPdfWordingChangeEnabled ? 'Bewertung generieren' : 'Bewertungs-PDF generieren'}
                </Button>
              </div>
            </div>
          )}
        </div>
      )}

      {!isEligibleForValuationPdf() && (
        <div className='grid-flex grid-justify-center' data-testing='response-container'>
          <div data-testid='response-sub-headline' className='sub-headline grid-item one-whole align-center'>
            <Font color='regular'>{getStatusMessageSubHeadLine(realEstateType, intent)}</Font>
          </div>

          {realEstateType !== RealEstateType.COMMERCIAL && (
            <div data-testid='valuation-div' className='grid-flex grid-justify-center'>
              <div className='grid-item one-whole border-bottom' />
              <div className='second-headline grid-item one-whole padding-bottom-s align-center'>
                <Font>{getRealEstateTypeValueHeader(realEstateType)}</Font>
              </div>
              <div data-testid='response-valuation' className='grid-item padding-bottom-l align-center'>
                <Heading4 tag='div' weight='bold' className={`${!showHighlighter ? '' : 'highlighter'}`}>
                  {valuation.data ? valuationAsString(value, min, max, prevRange) : 'z.Z. keine Bewertung verfügbar'}
                </Heading4>
              </div>

              {prevRange && (
                <div
                  data-testid='single-valuation-message'
                  className='grid-item one-whole align-center padding-top-xs margin-bottom-xs padding-bottom-m'
                >
                  <Font color='regular'>
                    {getRealEstateTypeValueHeaderLong(realEstateType)}
                    <br /> zwischen {getRangeValuationString(min, max)}{' '}
                  </Font>
                </div>
              )}

              <div className='grid-item one-whole align-center padding-top-s'>
                <i className='font-xl is24-icon-message-email' />
              </div>
              <div className='grid-item one-whole align-center'>
                <Font color={fontColorRegularDefault}>{getInstructionText(intent)}</Font>
              </div>
            </div>
          )}

          <div className='grid-item one-whole padding-top-xl'>
            {isIv24Integration ? '' : <ProductBox intent={intent} />}
          </div>

          {realEstateType !== RealEstateType.COMMERCIAL && (
            <div className='grid-flex grid-justify-center padding-top-s'>
              <div data-testid='valuation-info-div' className='grid-item one-whole note align-center padding-xl'>
                <Font className='font-xs' color={fontColorRegularDefault}>
                  *Eine verlässliche Marktwertermittlung kann nur von einem Experten vor Ort{' '}
                  <br className='palm-hide' /> vorgenommen werden.
                </Font>
              </div>
            </div>
          )}
        </div>
      )}
    </StepTemplate>
  )
}

export default ResponseStep
